import { type CSSProperties, useMemo, type SVGAttributes } from 'react'
import { Box, type BoxProps } from '../Box'
import {
  miniChartCss,
  lineColorVar,
  miniChartContainerCss,
} from './MiniChart.css'

type BaseMiniChartProps = {
  data: Array<number>
  positive?: boolean
  negative?: boolean
  width?: CSSProperties['width']
  height?: CSSProperties['height']
  paddingX?: BoxProps['paddingX']
  paddingY?: BoxProps['paddingY']
  lineThickness?: number
  scale?: number // how much difference do we want to have between the highest and lowest point
  preserveAspectRatio?: SVGAttributes<SVGElement>['preserveAspectRatio']
  className?: string
}

type MiniChartProps = Omit<BaseMiniChartProps, 'paddingX'> & {
  size: 's' | 'm'
}

export function MiniChart({ size, ...props }: MiniChartProps) {
  let paddingX: BoxProps['paddingX'] = 'xs'
  if (size === 's') {
    paddingX = 'none'
  }

  // Plot should never be completely side to side/top to top, hence paddings
  return <BaseMiniChart paddingX={paddingX} paddingY="xxxs" {...props} />
}

function BaseMiniChart({
  data,
  positive,
  negative,
  width,
  height,
  lineThickness = 1.5,
  scale = 10,
  preserveAspectRatio,
  className,
  ...props
}: BaseMiniChartProps) {
  const dataPoints = useMemo(() => {
    // normalize data so the y-axis is always centered
    const min = Math.min(...data)
    const max = Math.max(...data)

    return data.map((value, index) => {
      const x = index * 10
      const tempY =
        max === min
          ? scale / 2
          : Math.round(((value - min) / (max - min)) * scale * 100) / 100

      const y = scale - tempY

      return `${x},${y}`
    })
  }, [data, scale])

  return (
    <Box
      className={[miniChartContainerCss, className]}
      style={{ width, height }}
      position="relative"
      {...props}
      data-trend={positive ? 'positive' : negative ? 'negative' : undefined}
    >
      <Box
        display="flex"
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
      >
        <svg
          fill="none"
          viewBox={`0 0 ${dataPoints.length * 5} ${scale}`}
          className={miniChartCss}
          preserveAspectRatio={preserveAspectRatio}
        >
          <path
            d={`M ${dataPoints.join(' ')}`}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            stroke={lineColorVar}
            vectorEffect="non-scaling-stroke"
            strokeWidth={lineThickness}
          />
        </svg>
      </Box>
    </Box>
  )
}
